import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  ExclamationCircleOutlined,
  EyeOutlined,
  HolderOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Card,
  Col,
  Dropdown,
  GetProp,
  MenuProps,
  Modal,
  Row,
  Table,
  TablePaginationConfig,
  TableProps,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import Search from 'antd/es/input/Search';
import { SearchProps } from 'antd/lib/input';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Layout from '../../../components/Layout/Layout';
import appI18n from '../../../config/i18n';
import { ApiErrorData } from '../../../models/ApiResponse';
import { Company } from '../../../models/Company';
import { Contact } from '../../../models/Contact';
import { Country } from '../../../models/Country';
import { CompanyService } from '../../../services/CompanyService';
import { ContactService } from '../../../services/ContactService';
import { CountryService } from '../../../services/CountryService';
import { FunctionsHelper } from '../../../utils/FunctionsHelper';
import ContactCompaniesTags from './components/ContactCompaniesTags';
import DetailContactModal from './components/DetailContactModal';
import NewContactModal from './components/NewContactModal';

import './Contacts.scss';

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

type MainType = Contact;

const PAGE_SIZE = 10;

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const CustomRow: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

function Contacts() {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, contextModalHolder] = Modal.useModal();
  const [tableLoading, setTableLoading] = useState(true);
  const [dataSource, setDataSource] = useState<MainType[]>([]);
  const [filteredDataSource, setFilteredDataSource] = useState<MainType[]>([]);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
  });
  const [title] = useState<string>('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const dateFormat: string = useSelector(
    (state: any) => state.intl?.dateFormat
  );

  const [selectedRow, setSelectedRow] = useState<MainType>();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isNewResourceModalOpen, setIsNewResourceModalOpen] = useState(false);

  /* Filters */
  const [term, setTerm] = useState('');

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    fetchData(tableParams.pagination?.current);
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams.sortField,
    tableParams.sortOrder,
  ]);

  useEffect(() => {
    fetchData(1);
  }, [term]);

  const init = async () => {
    setTableLoading(true);

    const [countryResponse, companyResponse] = await Promise.all([
      CountryService.getAll(),
      CompanyService.getAll(),
      fetchDataFromServer(false),
    ]);

    if (countryResponse.success && companyResponse.success) {
      setCountries(countryResponse.data as Country[]);
      setCompanies(companyResponse.data as Company[]);
    } else {
      if (!countryResponse.success) {
        const error = countryResponse.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t('scenes.settings.contacts.scene.errors.fetch_countries'),
          3.5
        );
      }

      if (!companyResponse.success) {
        const error = companyResponse.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t('scenes.settings.contacts.scene.errors.fetch_companies'),
          3.5
        );
      }
    }

    setTableLoading(false);
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    setTerm(value);
  };

  const createResourceModal = () => {
    setIsNewResourceModalOpen(true);
  };

  const showDetailResource = async (row: MainType) => {
    setSelectedRow(row);
    setIsDetailModalOpen(true);
  };

  const handleCloseNewResourceModal = () => {
    setIsNewResourceModalOpen(false);
  };

  const handleCloseShowDetailResourceModal = () => {
    setSelectedRow(undefined);
    setIsDetailModalOpen(false);
  };

  const getDropdownMenu = (row: MainType): MenuProps['items'] => {
    const menus: MenuProps['items'] = [];
    let itemNro = 1;

    menus.push({
      key: `${itemNro++}`,
      label: (
        <span>
          <EyeOutlined style={{ marginRight: '8px' }} />{' '}
          {t('scenes.settings.contacts.scene.dropdown.view_detail')}
        </span>
      ),
      onClick: () => {
        showDetailResource(row);
      },
    });

    menus.push({
      key: `${itemNro++}`,
      label: (
        <span>
          <LockOutlined style={{ marginRight: '8px' }} />{' '}
          {t('scenes.settings.contacts.scene.dropdown.delete')}
        </span>
      ),
      onClick: async () => {
        await deleteResource(row);
      },
      danger: true,
    });

    return menus;
  };

  const deleteResource = async (row: MainType) => {
    modal.confirm({
      title: t('scenes.settings.contacts.scene.delete_confirm.title'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <span>
          {t('scenes.settings.contacts.scene.delete_confirm.content', {
            id: row.id,
          })}
        </span>
      ),
      okText: t('scenes.settings.contacts.scene.delete_confirm.ok'),
      cancelText: t('scenes.settings.contacts.scene.delete_confirm.cancel'),
      onOk: async () => {
        setTableLoading(true);
        const response = await ContactService.delete(row.id);

        if (response.success) {
          await fetchDataFromServer();
          messageApi.success(
            <span>
              {t('scenes.settings.contacts.scene.messages.delete_success', {
                id: row.id,
              })}
            </span>
          );
        } else {
          const error = response.data as ApiErrorData;
          messageApi.error(
            (error.message as string) ||
              t('scenes.settings.contacts.scene.errors.delete_failed'),
            3.5
          );
        }

        setTableLoading(false);
      },
    });
  };

  const handleTableChange: TableProps<MainType>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    const sorterLocal = sorter as any;

    setTableParams({
      pagination,
      filters,
      sortOrder:
        sorterLocal.order && sorterLocal.columnKey
          ? sorterLocal.order
          : undefined,
      sortField:
        sorterLocal.order && sorterLocal.columnKey
          ? sorterLocal.columnKey
          : undefined,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setFilteredDataSource([]);
    }
  };

  const fetchDataFromServer = async (withLoading = false) => {
    if (withLoading) {
      setTableLoading(true);
    }
    const response = await ContactService.getAll();

    if (response.success) {
      setDataSource(response.data as MainType[]);
      fetchData(1, response.data as MainType[]);
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.contacts.scene.errors.fetch_contacts'),
        3.5
      );
    }

    if (withLoading) {
      setTableLoading(false);
    }
  };

  const fetchData = (currentPage = 1, data?: MainType[]) => {
    const termFilter = FunctionsHelper.normalizeText(term.trim());

    let filteredData = data !== undefined ? data : [...dataSource];

    if (termFilter.length > 0) {
      filteredData = filteredData.filter((record) => {
        return (
          FunctionsHelper.normalizeText(record.fullName).includes(termFilter) ||
          FunctionsHelper.normalizeText(record.email).includes(termFilter)
        );
      });
    }

    const total = filteredData.length;

    setFilteredDataSource(filteredData);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: currentPage,
        total: total,
      },
    });
  };

  const getCountryName = (countryCode: string) => {
    const country = countries.find((record) => {
      return record.code === countryCode;
    });

    return country ? country.name : countryCode;
  };

  const columns: TableColumnsType<MainType> = [
    {
      key: 'sort',
      align: 'center',
      width: 80,
      render: () => <DragHandle />,
    },
    {
      title: t('scenes.settings.contacts.scene.table.columns.id'),
      dataIndex: 'id',
      key: 'id',
      width: 60,
      align: 'center',
    },
    {
      title: t('scenes.settings.contacts.scene.table.columns.name'),
      dataIndex: 'fullName',
      key: 'fullName',
      ellipsis: true,
    },
    {
      title: t('scenes.settings.contacts.scene.table.columns.email'),
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      key: 'country',
      title: t('scenes.settings.contacts.scene.table.columns.country'),
      align: 'center',
      width: 140,
      render: (row: MainType) => <span>{getCountryName(row.countryCode)}</span>,
    },
    {
      key: 'company',
      title: t('scenes.settings.contacts.scene.table.columns.company'),
      align: 'center',
      render: (row: MainType) => {
        return (
          <ContactCompaniesTags
            companyIds={row.companyIds}
            companies={companies}
          />
        );
      },
    },
    {
      key: 'createdAtFormatted',
      title: t('scenes.settings.contacts.scene.table.columns.created_at'),
      dataIndex: 'createdAt',
      width: 160,
      align: 'center',
      render: (createdAt: Date) => {
        return <span>{dayjs(createdAt).format(dateFormat)}</span>;
      },
    },
    {
      key: 'actions',
      width: 120,
      align: 'center',
      render: (row: MainType) => {
        return (
          <Dropdown
            menu={{ items: getDropdownMenu(row) }}
            placement="bottomLeft"
            trigger={['click']}
          >
            <Button size="small">
              <MoreOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const originalDataSource = [...prevState];
        const activeIndex = prevState.findIndex(
          (record) => record.id === active?.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.id === over?.id
        );
        const newDataSource = arrayMove(prevState, activeIndex, overIndex);
        fetchData(tableParams.pagination?.current, newDataSource);
        handleDragEndUpdate(newDataSource, originalDataSource);
        return newDataSource;
      });
    }
  };

  const handleDragEndUpdate = async (
    newDataSource: MainType[],
    originalDataSource: MainType[]
  ) => {
    setTableLoading(true);

    const contactIds = newDataSource.map((contact) => contact.id);
    const response = await ContactService.uploadPositions(contactIds);

    setTableLoading(false);

    if (!response.success) {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.contacts.scene.errors.update_positions_failed'),
        3.5
      );
      fetchData(tableParams.pagination?.current, originalDataSource);
      setDataSource(originalDataSource);
    }
  };

  const createNewResource = async (
    countryCode: string,
    companyIds: number[],
    fullName: string,
    position: string,
    email: string,
    dialCode: string,
    phone: string,
    avatarUrl: string
  ) => {
    setTableLoading(true);

    const response = await ContactService.create({
      companyIds,
      countryCode,
      fullName,
      position,
      dialCode,
      phone,
      email,
      avatarUrl,
      description: null,
    });

    if (response.success) {
      await fetchDataFromServer(true);
      messageApi.success(
        t('scenes.settings.contacts.scene.messages.create_success', {
          id: (response.data as { createdId: number }).createdId,
        })
      );
      handleCloseNewResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.contacts.scene.errors.create_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  const editNewResource = async (
    id: number,
    companyIds: number[],
    fullName: string,
    position: string,
    email: string,
    dialCode: string,
    phone: string,
    avatarUrl: string
  ) => {
    setTableLoading(true);

    const response = await ContactService.update(id, {
      companyIds,
      fullName,
      position,
      dialCode,
      phone,
      email,
      avatarUrl,
      description: null,
    });

    if (response.success) {
      await fetchDataFromServer(true);
      messageApi.success(
        t('scenes.settings.contacts.scene.messages.update_success', { id })
      );
      handleCloseShowDetailResourceModal();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.settings.contacts.scene.errors.update_failed'),
        3.5
      );
    }

    setTableLoading(false);
  };

  return (
    <Layout title={title}>
      {contextHolder}
      {contextModalHolder}

      <Card>
        <div className="filter-container">
          <Row gutter={[16, 12]} align="middle">
            <Col flex="none">
              <label className="filter-search-container">
                {t('scenes.settings.contacts.scene.filters.label')}{' '}
                <Search
                  placeholder={t(
                    'scenes.settings.contacts.scene.filters.search_placeholder_name_or_email'
                  )}
                  onSearch={onSearch}
                  disabled={tableLoading}
                  allowClear
                />
              </label>
            </Col>
            <Col flex="auto" />
            <Col flex="none" className="filter-buttons-container">
              <Button
                type="primary"
                onClick={createResourceModal}
                disabled={tableLoading}
              >
                <PlusOutlined />{' '}
                {t('scenes.settings.contacts.scene.buttons.create_contact')}
              </Button>
            </Col>
          </Row>
        </div>
      </Card>

      <Card style={{ marginTop: '12px' }}>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={dataSource.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table<MainType>
              dataSource={filteredDataSource}
              bordered
              loading={tableLoading}
              size="small"
              scroll={{ x: 1300, y: 640 }}
              pagination={false}
              rowKey={(record) => record.id}
              onChange={handleTableChange}
              columns={columns}
              components={{ body: { row: CustomRow } }}
            />
          </SortableContext>
        </DndContext>
      </Card>

      {/* Modals */}
      <NewContactModal
        isOpen={isNewResourceModalOpen}
        handleModalCancel={handleCloseNewResourceModal}
        submit={createNewResource}
        countries={countries}
        companies={companies}
      />
      {selectedRow && (
        <DetailContactModal
          id={selectedRow.id}
          isOpen={isDetailModalOpen}
          handleModalCancel={handleCloseShowDetailResourceModal}
          submit={editNewResource}
          countries={countries}
          companies={companies}
        />
      )}
    </Layout>
  );
}

export default Contacts;
